import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import Icon from 'components/Icon';

const Row = styled.div`
  display: flex;
  gap: 12px;
  padding: 14px 18px;
  background: #ffffff;
  border-left: 2px solid ${props => (props.$failed ? '#C7362F' : 'transparent')};

  &:not(:last-child) {
    border-bottom: 1px solid #eef0f2;
  }
`;

const StatusMarker = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 9999px;
  background: ${props => (props.$passed ? '#1E9331' : '#C7362F')};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
`;

const TextColumn = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const CheckLabel = styled.div`
  color: #1a1a1a;
  font-size: 14px;
  font-family: Gotham, sans-serif;
  font-weight: 400;
  line-height: 20px;
`;

const Summary = styled.div`
  color: ${props => (props.$failed ? '#B03029' : '#6b6e72')};
  font-size: 13px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
  line-height: 19px;
  word-wrap: break-word;
`;

const VitalBlock = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const VitalLine = styled.div`
  color: #3f4145;
  font-size: 13px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
  line-height: 19px;
  word-wrap: break-word;
`;

const DetailsBlock = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatsLine = styled.div`
  color: #8a8d90;
  font-size: 12px;
  font-family: Gotham, sans-serif;
  font-weight: 325;
  line-height: 18px;
  word-wrap: break-word;
`;

const ToggleButton = styled.button`
  align-self: flex-start;
  margin-top: 6px;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: #6b6e72;
  font-size: 12px;
  font-family: Gotham, sans-serif;
  font-weight: 350;
  line-height: 16px;

  &:hover {
    color: #1a1a1a;
  }
`;

const Chevron = styled.span`
  display: inline-flex;
  transform: ${props => (props.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.15s ease;
`;

const SKIPPED_EVIDENCE_KEYS = new Set([
  'metric',
  'blocked_by',
  'origin',
  'device_id',
  'error',
  'inlet_pressure_key',
  'outlet_pressure_key',
  'comparison',
  'failure_reason',
  'failed_sides',
  'source',
  'reason',
]);

function formatPrimitive(value) {
  if (value == null) return null;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
  }
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}

/** Unix seconds (or ms) → local datetime string for dashboards */
function formatUnixTimestamp(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return formatPrimitive(raw);
  const sec = n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
  // Ignore clearly invalid unix seconds
  if (sec < 946684800 || sec > 4102444800) return formatPrimitive(raw);
  return moment.unix(sec).format('MM/DD/YYYY h:mm:ss A');
}

function formatVerificationWindow(fromRaw, toRaw) {
  const a = Number(fromRaw);
  const b = Number(toRaw);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const s1 = a > 1e12 ? Math.floor(a / 1000) : Math.floor(a);
  const s2 = b > 1e12 ? Math.floor(b / 1000) : Math.floor(b);
  if (s1 < 946684800 || s2 < 946684800) return null;
  const m1 = moment.unix(s1);
  const m2 = moment.unix(s2);
  if (m1.isSame(m2, 'day')) {
    return `Verification window: ${m1.format('MM/DD/YYYY')} · ${m1.format(
      'h:mm:ss A',
    )} – ${m2.format('h:mm:ss A')}`;
  }
  return `Verification window: ${m1.format('MM/DD/YYYY h:mm:ss A')} – ${m2.format(
    'MM/DD/YYYY h:mm:ss A',
  )}`;
}

/**
 * Builds readable stat lines from API `evidence` (sensor values, thresholds, times),
 * categorized into:
 *  - primary: the headline measured value / expected threshold (the gist of the check)
 *  - vital: supporting line worth surfacing even for passing checks
 *  - details: everything else (windows, timestamps, raw readings, data age)
 */
function buildEvidenceStatLines(evidence) {
  const primary = [];
  const vital = [];
  const details = [];

  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) {
    return { primary, vital, details };
  }

  const usedKeys = new Set();

  const pushPrimary = text => {
    if (text) primary.push(text);
  };
  const pushVital = text => {
    if (text) vital.push(text);
  };
  const pushDetail = text => {
    if (text) details.push(text);
  };

  if (
    evidence.inlet_pressure_value != null ||
    evidence.outlet_pressure_value != null ||
    evidence.pressure_sum != null
  ) {
    ['inlet_pressure_value', 'outlet_pressure_value', 'pressure_sum'].forEach(k => usedKeys.add(k));
    if (evidence.inlet_pressure_value != null) {
      pushPrimary(`Inlet pressure: ${formatPrimitive(evidence.inlet_pressure_value)}`);
    }
    if (evidence.outlet_pressure_value != null) {
      pushPrimary(`Outlet pressure: ${formatPrimitive(evidence.outlet_pressure_value)}`);
    }
    if (evidence.pressure_sum != null) {
      pushVital(`Sum (inlet + outlet, reference only): ${formatPrimitive(evidence.pressure_sum)}`);
    }
    if (evidence.inlet_ok != null) {
      usedKeys.add('inlet_ok');
      pushDetail(`Inlet passes threshold: ${formatPrimitive(evidence.inlet_ok)}`);
    }
    if (evidence.outlet_ok != null) {
      usedKeys.add('outlet_ok');
      pushDetail(`Outlet passes threshold: ${formatPrimitive(evidence.outlet_ok)}`);
    }
    if (evidence.threshold != null) {
      usedKeys.add('threshold');
      usedKeys.add('operator');
      const thr = formatPrimitive(evidence.threshold);
      const op = evidence.operator ? String(evidence.operator).trim() : '>';
      const perSensor = evidence.comparison === 'each_sensor_vs_threshold';
      if (perSensor) {
        pushVital(`Expected: each of inlet (P-1) and outlet (P-2) ${op} ${thr}`);
      } else {
        pushVital(`Expected: combined pressure ${op} ${thr}`);
      }
    }
  } else if (evidence.comparison === 'raw_instant_value_vs_threshold') {
    // RO: pass/fail on pressure_*_value; `value` is PSI for display.
    usedKeys.add('comparison');
    if (evidence.value != null && evidence.value_unit === 'psi') {
      usedKeys.add('value');
      usedKeys.add('value_unit');
      pushPrimary(`Calculated pressure: ${formatPrimitive(evidence.value)} PSI`);
    } else if (evidence.value != null) {
      usedKeys.add('value');
      if (evidence.value_unit) {
        usedKeys.add('value_unit');
        pushPrimary(
          `Calculated: ${formatPrimitive(evidence.value)} ${String(
            evidence.value_unit,
          ).toUpperCase()}`,
        );
      } else {
        pushPrimary(`Calculated: ${formatPrimitive(evidence.value)}`);
      }
    }
    if (evidence.raw_value != null) {
      usedKeys.add('raw_value');
      const part = evidence.raw_metric ? ` ${String(evidence.raw_metric)}` : '';
      const op = evidence.operator ? String(evidence.operator).trim() : '>';
      const rawThr =
        evidence.raw_threshold != null
          ? formatPrimitive(evidence.raw_threshold)
          : evidence.threshold != null
          ? formatPrimitive(evidence.threshold)
          : null;
      if (rawThr != null) {
        usedKeys.add('raw_threshold');
        usedKeys.add('threshold');
        usedKeys.add('operator');
        pushVital(
          `Check uses raw reading${part}: ${formatPrimitive(
            evidence.raw_value,
          )} (expected ${op} ${rawThr})`,
        );
      } else {
        pushVital(`Raw reading${part}: ${formatPrimitive(evidence.raw_value)}`);
      }
    }
    if (evidence.from != null && evidence.to != null) {
      usedKeys.add('from');
      usedKeys.add('to');
      pushDetail(formatVerificationWindow(evidence.from, evidence.to));
    }
    if (evidence.timestamp != null) {
      usedKeys.add('timestamp');
      const ts = formatUnixTimestamp(evidence.timestamp);
      if (ts) {
        pushDetail(`Sample timestamp: ${ts}`);
      }
    }
  } else if (evidence.flow_value != null) {
    usedKeys.add('flow_value');
    usedKeys.add('threshold');
    usedKeys.add('operator');
    const thr = evidence.threshold != null ? formatPrimitive(evidence.threshold) : null;
    const op = evidence.operator ? String(evidence.operator).trim() : '>';
    const flow = formatPrimitive(evidence.flow_value);
    if (thr != null) {
      pushPrimary(`Flow reading: ${flow} (expected ${op} ${thr})`);
    } else {
      pushPrimary(`Flow reading: ${flow}`);
    }
    if (evidence.from != null && evidence.to != null) {
      usedKeys.add('from');
      usedKeys.add('to');
      pushDetail(formatVerificationWindow(evidence.from, evidence.to));
    }
    if (evidence.timestamp != null) {
      usedKeys.add('timestamp');
      const ts = formatUnixTimestamp(evidence.timestamp);
      if (ts) pushDetail(`Sample timestamp: ${ts}`);
    }
  } else if (evidence.bypass_value != null) {
    usedKeys.add('bypass_value');
    const flowKeyLabel = evidence.flow_key ? ` (${evidence.flow_key})` : '';
    pushPrimary(`Bypass flow value${flowKeyLabel}: ${formatPrimitive(evidence.bypass_value)}`);
    if (evidence.flow_key != null) {
      usedKeys.add('flow_key');
    }
    if (evidence.threshold != null) {
      usedKeys.add('threshold');
      usedKeys.add('operator');
      const op = evidence.operator ? String(evidence.operator).trim() : '>';
      pushVital(`Expected: ${op} ${formatPrimitive(evidence.threshold)}`);
    }
    if (evidence.bypass_total != null) {
      usedKeys.add('bypass_total');
      pushVital(`Bypass flow total pulses: ${formatPrimitive(evidence.bypass_total)}`);
    }
  } else if (evidence.value != null && evidence.metric != null && evidence.threshold != null) {
    // Single-reading RO flow checks (permeate production, RO feed flow detection):
    // label clearly by metric instead of falling through to a generic "Value: X" line.
    usedKeys.add('value');
    usedKeys.add('metric');
    usedKeys.add('threshold');
    usedKeys.add('operator');
    const thr = formatPrimitive(evidence.threshold);
    const op = evidence.operator ? String(evidence.operator).trim() : '>';
    const metricLabel = String(evidence.metric).replace(/_/g, ' ');
    const capitalizedMetric = `${metricLabel.charAt(0).toUpperCase()}${metricLabel.slice(1)}`;
    pushPrimary(`${capitalizedMetric}: ${formatPrimitive(evidence.value)} (expected ${op} ${thr})`);
    if (evidence.from != null && evidence.to != null) {
      usedKeys.add('from');
      usedKeys.add('to');
      pushDetail(formatVerificationWindow(evidence.from, evidence.to));
    }
    if (evidence.timestamp != null) {
      usedKeys.add('timestamp');
      const ts = formatUnixTimestamp(evidence.timestamp);
      if (ts) pushDetail(`Sample timestamp: ${ts}`);
    }
  }

  if (evidence.age_minutes != null) {
    usedKeys.add('age_minutes');
    pushDetail(`Data age: ${formatPrimitive(evidence.age_minutes)} minutes`);
  }
  if (evidence.age_seconds != null && evidence.age_minutes == null) {
    usedKeys.add('age_seconds');
    pushDetail(`Data age: ${formatPrimitive(evidence.age_seconds)} seconds`);
  }
  if (evidence.last_timestamp != null && !usedKeys.has('last_timestamp')) {
    usedKeys.add('last_timestamp');
    const ts = formatUnixTimestamp(evidence.last_timestamp);
    if (ts) pushDetail(`Last IoT reading: ${ts}`);
  }
  if (evidence.timestamp != null && !usedKeys.has('timestamp') && evidence.flow_value == null) {
    usedKeys.add('timestamp');
    const ts = formatUnixTimestamp(evidence.timestamp);
    if (ts) pushDetail(`Reading time: ${ts}`);
  }
  if (evidence.from != null && evidence.to != null && !usedKeys.has('from')) {
    usedKeys.add('from');
    usedKeys.add('to');
    pushDetail(formatVerificationWindow(evidence.from, evidence.to));
  }

  Object.entries(evidence).forEach(([key, value]) => {
    if (SKIPPED_EVIDENCE_KEYS.has(key)) return;
    if (usedKeys.has(key)) return;
    if (value != null && typeof value === 'object') return;
    if (
      [
        'inlet_pressure_value',
        'outlet_pressure_value',
        'pressure_sum',
        'inlet_ok',
        'outlet_ok',
        'flow_value',
        'bypass_value',
        'bypass_total',
        'flow_key',
        'threshold',
        'operator',
        'age_minutes',
        'age_seconds',
        'last_timestamp',
        'timestamp',
        'from',
        'to',
      ].includes(key)
    ) {
      return;
    }
    const formatted =
      typeof value === 'number' && key.toLowerCase().includes('timestamp')
        ? formatUnixTimestamp(value) || formatPrimitive(value)
        : formatPrimitive(value);
    if (formatted != null) {
      const label = key.replace(/_/g, ' ');
      pushDetail(`${label.charAt(0).toUpperCase()}${label.slice(1)}: ${formatted}`);
    }
  });

  return { primary, vital, details };
}

function HealthCheckCheckItem({ label, passed, message, evidence }) {
  const [open, setOpen] = useState(false);
  const trimmedMessage = message != null ? String(message).trim() : '';
  const { primary, vital, details } = buildEvidenceStatLines(evidence);

  // One-line plain-English summary: API message wins, else the primary measured value.
  const summaryText = trimmedMessage || primary[0] || null;

  // Lines beyond the summary headline that count as "vital" (shown even when passing).
  const headlineUsedFromPrimary = !trimmedMessage && primary.length > 0;
  const vitalLines = [...(headlineUsedFromPrimary ? primary.slice(1) : primary), ...vital];

  // Failed: surface everything automatically. Passed: vital up top, rest behind expander.
  const hasMore = details.length > 0;
  const showDetails = passed ? open : true;

  return (
    <Row $failed={!passed}>
      <StatusMarker $passed={passed}>
        <Icon name={passed ? 'check' : 'times'} fontSize={9} color="white" />
      </StatusMarker>
      <TextColumn>
        <CheckLabel>{label}</CheckLabel>

        {summaryText ? <Summary $failed={!passed}>{summaryText}</Summary> : null}

        {vitalLines.length > 0 ? (
          <VitalBlock>
            {vitalLines.map((line, i) => (
              <VitalLine key={`vital-${line}-${i}`}>{line}</VitalLine>
            ))}
          </VitalBlock>
        ) : null}

        {passed && hasMore ? (
          <ToggleButton type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}>
            {open ? 'Hide details' : 'Show details'}
            <Chevron $open={open}>
              <Icon name="chevron-down" fontSize={9} color="#6b6e72" />
            </Chevron>
          </ToggleButton>
        ) : null}

        {showDetails && details.length > 0 ? (
          <DetailsBlock>
            {details.map((line, i) => (
              <StatsLine key={`detail-${line}-${i}`}>{line}</StatsLine>
            ))}
          </DetailsBlock>
        ) : null}
      </TextColumn>
    </Row>
  );
}

HealthCheckCheckItem.propTypes = {
  evidence: PropTypes.object,
  label: PropTypes.string.isRequired,
  message: PropTypes.string,
  passed: PropTypes.bool.isRequired,
};

HealthCheckCheckItem.defaultProps = {
  evidence: null,
  message: null,
};

export default HealthCheckCheckItem;
